import { Feather } from "@expo/vector-icons";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useCallback, useMemo, useState } from "react";
import {
  FlatList,
  Image,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from "react-native";
import { RootStackParamList } from "../../navigation/main";
import { t } from "../../i18n/strings";
import { fetchColumnVideos } from "../../services/videoServer";
import type { MediaVariant, ProductPlacement } from "../../../types";
import styles from "./styles";

type CategoryKey =
  | "all"
  | "freshVideos"
  | "games"
  | "music"
  | "shorts"
  | "live";

export type ColumnVideo = {
  id: string;
  title: string;
  channel: string;
  channelId?: string;
  meta: string;
  thumbnail: string;
  portraitThumbnail?: string;
  subtitleUrl?: string;
  videoUrl: string;
  mobileVideoUrl?: string;
  desktopVideoUrl?: string;
  backgroundMediaUrl?: string;
  mediaVariants?: MediaVariant[];
  productPlacements?: ProductPlacement[];
  productEditorAllowed?: boolean;
  p2pEnabled?: boolean;
  p2pConfigured?: boolean;
  p2pViewerCount?: number;
  tags?: string[];
  rankingScore?: number;
  isAdvertisement?: boolean;
  adDisplayMode?: "smart" | "keyword" | "broad";
  adMaxPlays?: number;
  adPlayCount?: number;
  adRemainingPlays?: number;
  adKeywords?: string[];
  isPaid?: boolean;
  coinPrice?: number;
  vipOnly?: boolean;
  vipLocked?: boolean;
  ranking?: {
    mode: string;
    position: number;
    publicScore: number;
    personalizedScore: number;
    personalizationConfidence: number;
    userSignals: {
      clicks: number;
      watchSeconds: number;
      favorited: boolean;
      commented: boolean;
      tagAffinity: number;
      skips?: number;
    };
    reason: string;
    metrics: { clicks: number; watchSeconds: number; comments: number };
  };
  duration: string;
  category: CategoryKey;
  createdAt?: string;
  updatedAt?: string;
};

const categories: { key: CategoryKey; label: string }[] = [
  { key: "all", label: t("all") },
  { key: "freshVideos", label: t("freshVideos") },
  { key: "games", label: t("games") },
  { key: "music", label: t("music") },
  { key: "shorts", label: t("shorts") },
  { key: "live", label: t("live") },
];

const PAGE_SIZE = 60;

function VideoCard({
  item,
  onPress,
}: {
  item: ColumnVideo;
  onPress: () => void;
}) {
  return (
    <Pressable style={styles.card} onPress={onPress}>
      <View style={styles.thumbnailWrap}>
        <Image source={{ uri: item.thumbnail }} style={styles.thumbnail} />
        <Text style={styles.duration}>{item.duration}</Text>
      </View>
      <View style={styles.cardBody}>
        <Text style={styles.title} numberOfLines={2}>
          {item.title}
        </Text>
        <Feather name="more-vertical" size={18} color="#d6d6d6" />
      </View>
      <Text style={styles.meta} numberOfLines={2}>
        {item.channel} - {item.meta}
      </Text>
    </Pressable>
  );
}

export default function ColumnsScreen() {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const [activeCategory, setActiveCategory] = useState<CategoryKey>("all");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [notificationsOn, setNotificationsOn] = useState(false);
  const [bannerText, setBannerText] = useState("");
  const [serverVideos, setServerVideos] = useState<ColumnVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useFocusEffect(
    useCallback(() => {
      let active = true;
      setLoading(true);
      setLoadError(false);
      setHasMore(true);

      fetchColumnVideos({ limit: PAGE_SIZE, offset: 0 })
        .then((items) => {
          if (active) {
            setServerVideos(items);
            setHasMore(items.length === PAGE_SIZE);
          }
        })
        .catch(() => {
          if (active) {
            setServerVideos([]);
            setLoadError(true);
          }
        })
        .finally(() => {
          if (active) setLoading(false);
        });

      return () => {
        active = false;
      };
    }, [retry]),
  );

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    setLoadingMore(true);
    fetchColumnVideos({ limit: PAGE_SIZE, offset: serverVideos.length })
      .then((items) => {
        setServerVideos((current) => {
          const known = new Set(current.map((video) => video.id));
          return [...current, ...items.filter((video) => !known.has(video.id))];
        });
        setHasMore(items.length === PAGE_SIZE);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoadingMore(false));
  }, [hasMore, loading, loadingMore, serverVideos.length]);

  const videos = serverVideos;

  const filteredVideos = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    return videos.filter((video) => {
      const categoryMatch =
        activeCategory === "all" || video.category === activeCategory;
      const searchMatch =
        !query ||
        `${video.title} ${video.channel} ${video.meta}`
          .toLowerCase()
          .includes(query);

      return categoryMatch && searchMatch;
    });
  }, [activeCategory, searchText, videos]);

  const resetFilters = () => {
    setActiveCategory("all");
    setSearchText("");
    setBannerText("");
  };

  const toggleNotifications = () => {
    setNotificationsOn((current) => {
      const next = !current;
      setBannerText(
        next ? t("notificationsEnabled") : t("notificationsDisabled"),
      );
      return next;
    });
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.brand}>
          <View style={styles.brandMark}>
            <Feather name="grid" size={18} color="white" />
          </View>
          <Text style={styles.brandText}>{t("columnsTitle")}</Text>
        </View>
        <View style={styles.headerActions}>
          <Pressable
            style={styles.headerAction}
            onPress={() => setBannerText(t("castSearching"))}
          >
            <Feather name="cast" size={22} color="white" />
          </Pressable>
          <Pressable style={styles.headerAction} onPress={toggleNotifications}>
            <Feather
              name="bell"
              size={22}
              color={notificationsOn ? "#28c99a" : "#f2f5f4"}
            />
          </Pressable>
          <Pressable
            style={styles.headerAction}
            onPress={() => setSearchOpen((current) => !current)}
          >
            <Feather
              name="search"
              size={24}
              color={searchOpen ? "#28c99a" : "#f2f5f4"}
            />
          </Pressable>
        </View>
      </View>

      {searchOpen && (
        <View style={styles.searchRow}>
          <Feather name="search" size={18} color="#a8a8a8" />
          <TextInput
            value={searchText}
            onChangeText={setSearchText}
            placeholder={t("searchPlaceholder")}
            placeholderTextColor="#8c8c8c"
            style={styles.searchInput}
            autoFocus
          />
          {searchText.length > 0 && (
            <Pressable onPress={() => setSearchText("")}>
              <Feather name="x" size={18} color="#d6d6d6" />
            </Pressable>
          )}
        </View>
      )}

      {bannerText.length > 0 && (
        <Pressable style={styles.banner} onPress={() => setBannerText("")}>
          <Text style={styles.bannerText}>{bannerText}</Text>
          <Feather name="x" size={16} color="#f1f1f1" />
        </Pressable>
      )}

      <ScrollView
        style={styles.categoryScroller}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.categoryList}
      >
        <Pressable style={styles.compassChip} onPress={resetFilters}>
          <Feather name="compass" size={18} color="white" />
        </Pressable>
        {categories.map((category) => {
          const active = category.key === activeCategory;
          return (
            <Pressable
              key={category.key}
              onPress={() => setActiveCategory(category.key)}
              style={[styles.categoryChip, active && styles.categoryActive]}
            >
              <Text
                style={[
                  styles.categoryText,
                  active && styles.categoryTextActive,
                ]}
              >
                {category.label}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>

      <FlatList
        style={styles.videoList}
        data={filteredVideos}
        keyExtractor={(item) => item.id}
        numColumns={2}
        renderItem={({ item }) => (
          <VideoCard
            item={item}
            onPress={() =>
              navigation.navigate("columnVideo", {
                videos: filteredVideos,
                initialIndex: filteredVideos.findIndex(
                  (video) => video.id === item.id,
                ),
              })
            }
          />
        )}
        ListEmptyComponent={
          <View style={styles.empty}>
            {loadError ? (
              <Pressable onPress={() => setRetry((value) => value + 1)}>
                <Text style={styles.emptyText}>视频加载失败，点击重试</Text>
              </Pressable>
            ) : (
              <Text style={styles.emptyText}>
                {loading ? "正在加载视频…" : t("noVideos")}
              </Text>
            )}
          </View>
        }
        contentContainerStyle={styles.grid}
        columnWrapperStyle={filteredVideos.length > 0 ? styles.row : undefined}
        showsVerticalScrollIndicator={false}
        onEndReached={loadMore}
        onEndReachedThreshold={0.6}
        ListFooterComponent={
          loadingMore ? (
            <Text style={styles.emptyText}>正在加载更多视频…</Text>
          ) : null
        }
      />
    </View>
  );
}
